/** Standard "Copy" cell action for plain value columns (IDs, codes, emails,
 *  refs). `copyableColumn(col)` wraps a column that has NO custom renderCell:
 *  the value becomes a CellActionsPopover trigger with a single Copy action —
 *  ops paste IDs into Slack/tickets all day. Columns with their own renderCell
 *  should compose `<CopyableCell>` (or CellActionsPopover) themselves. */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';

import { useSnackbar } from '@/components/Snackbar';

import { CellActionsPopover } from './CellActionsPopover';

export function CopyableCell({ text }: { text: string }) {
  const snackbar = useSnackbar();
  if (!text) return null;
  return (
    <CellActionsPopover
      actions={[
        {
          key: 'copy',
          label: 'Copy',
          icon: <ContentCopyIcon fontSize="small" />,
          onClick: () =>
            void navigator.clipboard.writeText(text).then(
              () => snackbar.show(`Copied "${text}"`, 'success'),
              () => snackbar.show('Copy failed — clipboard unavailable', 'error'),
            ),
        },
      ]}
    >
      {text}
    </CellActionsPopover>
  );
}

export function copyableColumn<R extends GridValidRowModel>(col: GridColDef<R>): GridColDef<R> {
  return {
    ...col,
    renderCell: (p) => <CopyableCell text={p.value == null ? '' : String(p.value)} />,
  };
}
